import type { AvailableProjectWorktreesResult } from "@afar-research/contracts";
import { FolderGit2Icon, FolderGitIcon, FolderIcon } from "lucide-react";
import { memo, useMemo } from "react";

import {
  resolveCurrentWorkspaceLabel,
  resolveEnvModeLabel,
  resolveLockedWorkspaceLabel,
  type EnvMode,
} from "./BranchToolbar.logic";
import {
  Select,
  SelectGroup,
  SelectGroupLabel,
  SelectItem,
  SelectPopup,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Tooltip, TooltipPopup, TooltipTrigger } from "./ui/tooltip";
import { resolveAvailableWorktreeName } from "./BranchToolbarExistingWorktreeDialog";

const EMPTY_AVAILABLE_WORKTREES: AvailableProjectWorktreesResult["worktrees"] = [];

interface BranchToolbarEnvModeSelectorProps {
  envLocked: boolean;
  effectiveEnvMode: EnvMode;
  activeWorktreePath: string | null;
  activeWorktreeName?: string | null;
  onEnvModeChange: (mode: EnvMode) => void;
  availableWorktrees?: AvailableProjectWorktreesResult["worktrees"];
  onExistingWorktreeRequest?: () => void;
}

export const BranchToolbarEnvModeSelector = memo(function BranchToolbarEnvModeSelector({
  envLocked,
  effectiveEnvMode,
  activeWorktreePath,
  activeWorktreeName = null,
  onEnvModeChange,
  availableWorktrees = EMPTY_AVAILABLE_WORKTREES,
  onExistingWorktreeRequest,
}: BranchToolbarEnvModeSelectorProps) {
  const activeWorktree = useMemo(
    () => availableWorktrees.find((worktree) => worktree.path === activeWorktreePath) ?? null,
    [activeWorktreePath, availableWorktrees],
  );
  const envModeItems = useMemo(
    () => [
      { value: "local", label: resolveCurrentWorkspaceLabel(null) },
      { value: "worktree", label: resolveEnvModeLabel("worktree") },
      {
        value: "existing",
        label: activeWorktree ? resolveAvailableWorktreeName(activeWorktree) : "Existing worktree",
      },
    ],
    [activeWorktree],
  );

  const selectWorkspace = (value: string | null) => {
    if (value === null) return;
    if (value === "local" || value === "worktree") {
      onEnvModeChange(value);
      return;
    }
    if (value === "existing") onExistingWorktreeRequest?.();
  };

  if (envLocked) {
    if (activeWorktreePath) {
      const worktreeLabel =
        activeWorktreeName ??
        activeWorktree?.name ??
        activeWorktree?.branch ??
        resolveLockedWorkspaceLabel(activeWorktreePath);
      const tooltip = activeWorktreePath;

      return (
        <Tooltip>
          <TooltipTrigger
            render={
              <span
                aria-label={tooltip}
                className="inline-flex items-center gap-1 border border-transparent px-[calc(--spacing(3)-1px)] text-sm font-medium text-muted-foreground/70 sm:text-xs"
              />
            }
          >
            <FolderGitIcon className="size-3" />
            {worktreeLabel}
          </TooltipTrigger>
          <TooltipPopup side="top">{tooltip}</TooltipPopup>
        </Tooltip>
      );
    }

    return (
      <span className="inline-flex items-center gap-1 border border-transparent px-[calc(--spacing(3)-1px)] text-sm font-medium text-muted-foreground/70 sm:text-xs">
        <FolderIcon className="size-3" />
        {resolveLockedWorkspaceLabel(activeWorktreePath)}
      </span>
    );
  }

  return (
    <Select
      modal={false}
      value={activeWorktreePath ? "existing" : effectiveEnvMode}
      onValueChange={selectWorkspace}
      items={envModeItems}
    >
      <SelectTrigger variant="ghost" size="xs" className="font-medium" aria-label="Workspace">
        {effectiveEnvMode === "worktree" ? (
          <FolderGit2Icon className="size-3" />
        ) : activeWorktreePath ? (
          <FolderGitIcon className="size-3" />
        ) : (
          <FolderIcon className="size-3" />
        )}
        <SelectValue />
      </SelectTrigger>
      <SelectPopup>
        <SelectGroup>
          <SelectGroupLabel>Workspace</SelectGroupLabel>
          <SelectItem value="local">
            <span className="inline-flex items-center gap-1.5">
              <FolderIcon className="size-3" />
              {resolveCurrentWorkspaceLabel(null)}
            </span>
          </SelectItem>
          <SelectItem value="worktree">
            <span className="inline-flex items-center gap-1.5">
              <FolderGit2Icon className="size-3" />
              {resolveEnvModeLabel("worktree")}
            </span>
          </SelectItem>
        </SelectGroup>
        <SelectGroup>
          <SelectItem value="existing">
            <span className="inline-flex items-center gap-1.5">
              <FolderGitIcon className="size-3" />
              Existing worktree
            </span>
          </SelectItem>
        </SelectGroup>
      </SelectPopup>
    </Select>
  );
});
